import { RTC_SESSION_EVENTS, RTC_SESSION_MANAGER_EVENTS } from '../protocol/constants.js';
import { ValueStore } from '../util/emitter.js';
const uniqueSenders = (memberships) => [
    ...new Set(memberships.map(membership => membership.sender).filter(sender => !!sender)),
];
const earliestMembership = (memberships) => {
    const times = memberships.map(membership => membership.createdTs()).filter(time => Number.isFinite(time) && time > 0);
    return times.length > 0 ? Math.min(...times) : undefined;
};
const sameUsers = (left, right) => left.length === right.length && left.every((userId, index) => userId === right[index]);
export class RoomCallIndex {
    constructor(client) {
        this.client = client;
        this.store = new ValueStore(new Map());
        this.sessionListeners = new Map();
        this.startAnchors = new Map();
        this.getSnapshot = () => this.store.getSnapshot();
        this.subscribe = (listener) => this.store.subscribe(listener);
        this.onStarted = ((roomId, session) => this.track(roomId, session));
        this.onEnded = ((roomId) => this.untrack(roomId));
        client.matrixRTC.on(RTC_SESSION_MANAGER_EVENTS.sessionStarted, this.onStarted);
        client.matrixRTC.on(RTC_SESSION_MANAGER_EVENTS.sessionEnded, this.onEnded);
        client.getRooms().forEach(room => {
            const session = client.matrixRTC.getActiveRoomSession(room);
            if (session) {
                this.track(room.roomId, session);
            }
        });
    }
    summaryFor(roomId) {
        return this.store.getSnapshot().get(roomId);
    }
    dispose() {
        this.client.matrixRTC.off(RTC_SESSION_MANAGER_EVENTS.sessionStarted, this.onStarted);
        this.client.matrixRTC.off(RTC_SESSION_MANAGER_EVENTS.sessionEnded, this.onEnded);
        this.sessionListeners.forEach(({ session, listener }) => session.off(RTC_SESSION_EVENTS.membershipsChanged, listener));
        this.sessionListeners.clear();
    }
    track(roomId, session) {
        if (!this.sessionListeners.has(roomId)) {
            const listener = (() => this.refresh(roomId, session));
            session.on(RTC_SESSION_EVENTS.membershipsChanged, listener);
            this.sessionListeners.set(roomId, { session, listener });
        }
        this.refresh(roomId, session);
    }
    untrack(roomId) {
        const entry = this.sessionListeners.get(roomId);
        if (entry) {
            entry.session.off(RTC_SESSION_EVENTS.membershipsChanged, entry.listener);
            this.sessionListeners.delete(roomId);
        }
        this.startAnchors.delete(roomId);
        this.store.update(current => {
            if (!current.has(roomId)) {
                return current;
            }
            const next = new Map(current);
            next.delete(roomId);
            return next;
        });
    }
    refresh(roomId, session) {
        const memberships = session.memberships;
        if (memberships.length === 0) {
            this.untrack(roomId);
            return;
        }
        const me = this.client.getUserId();
        const myDevice = this.client.getDeviceId?.() ?? null;
        const intent = session.getConsensusCallIntent();
        const summary = {
            roomId,
            participantCount: memberships.length,
            userIds: uniqueSenders(memberships),
            intent: intent === 'audio' || intent === 'video' ? intent : undefined,
            includesMe: memberships.some(membership => membership.sender === me),
            onOtherDevice: !!myDevice && memberships.some(membership => membership.sender === me && membership.deviceId !== myDevice),
            startedAt: this.anchoredStart(roomId, earliestMembership(memberships)),
        };
        this.store.update(current => {
            const previous = current.get(roomId);
            if (previous &&
                previous.participantCount === summary.participantCount &&
                sameUsers(previous.userIds, summary.userIds) &&
                previous.intent === summary.intent &&
                previous.includesMe === summary.includesMe &&
                previous.onOtherDevice === summary.onOtherDevice &&
                previous.startedAt === summary.startedAt) {
                return current;
            }
            const next = new Map(current);
            next.set(roomId, summary);
            return next;
        });
    }
    anchoredStart(roomId, earliest) {
        const anchor = this.startAnchors.get(roomId);
        const startedAt = anchor === undefined ? earliest : Math.min(anchor, earliest ?? anchor);
        if (startedAt !== undefined) {
            this.startAnchors.set(roomId, startedAt);
        }
        return startedAt;
    }
}
